import React, { useCallback, useTransition, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
    saveContactInformation,
    hydrateContactData,
    updateOfferData,
    ContactData,
    QuoteData,
} from "store/application/applicationSlice";
import {
    checkIdentity,
    CheckIdentityRequestBody,
    storeApp as storeAppService,
    StoreAppRequestBody,
    getApplication as getApplicationService,
    generateFastPolicyNumber,
} from "services/onlineApplicationService";
import { createDate, toLocalISODateString } from "utils/age";
import { cleanPhone } from "utils/formatPhone";
import type {
    ContactInformationRef,
    StatementOfHealthRef,
    CoverageReplacementRef,
    PolicyBeneficiaryRef,
    SecondaryAddresseeRef,
} from "types/Card";
import { toSingleLetterFromLowerCaseGender } from "utils/conversions";
import { getClubData } from "services/cdsService";
import { toSpouseApplicationId } from "mappers/transformDTOSubmissionData";
import { transformGetDTOAppToContactData } from "mappers/transformGetDTOApp";
import { useProductContext, useProductConfig } from "context/ProductContext";
import type { ProductCodeEnum } from "state/RootSchema";

// ========== Types ==========

interface SubmissionResult {
    contactData: ContactData;
    needsReview: boolean;
}

interface ContactInformationRefs {
    contactInformation: React.RefObject<ContactInformationRef | null>;
    statementOfHealth: React.RefObject<StatementOfHealthRef | null> | null;
    coverageReplacement: React.RefObject<CoverageReplacementRef | null> | null;
    policyBeneficiary: React.RefObject<PolicyBeneficiaryRef | null>;
    secondaryAddressee: React.RefObject<SecondaryAddresseeRef | null>;
}

/**
 * Custom hook to manage contact information collection, identity check, and state persistence
 */
export const useContactInformation = () => {
    const dispatch = useAppDispatch();
    const { isMLTA, isDTO, isGIWL } = useProductContext();
    const productConfig = useProductConfig();

    // Selectors to get current state
    const contactData = useAppSelector((state) => state.application.contactData);
    const quoteData = useAppSelector((state) => state.application.quoteData);
    const offerData = useAppSelector((state) => state.application.offerData);
    const saveContId = useAppSelector((state) => state.application.saveContId);
    const needsReview = useAppSelector((state) => state.application.needsReview);
    const isContactDataComplete = useAppSelector((state) => state.application.isContactDataComplete);
    const [isLoadingData, startDataTransition] = useTransition();
    const [isLoadingSubmission, startSubmissionTransition] = useTransition();

    const saveContIdRef = useRef(saveContId);
    saveContIdRef.current = saveContId;

    /**
     * Persists the latest contact data after each card auto-save.
     * - DTO / MLTA: writes to the backend via storeApp.
     * - GIWL: writes to the backend via storeApp (userRef = saveContId) AND updates Redux
     *   via hydrateContactData (keeps the draft in sync across card transitions).
     */
    const saveApplicationData = useCallback(
        async (providedContactData: ContactData): Promise<void> => {
            const currentSaveContId = saveContIdRef.current;

            if (isGIWL) {
                // Always keep Redux in sync for GIWL (no finderNum to restore from backend).
                dispatch(hydrateContactData(providedContactData));
                if (!currentSaveContId) return;
                try {
                    const aaaMembershipNumber = providedContactData.contactInformation?.aaaMemberNumber || undefined;
                    const requestBody = createStoreAppRequest(providedContactData, {
                        userRef: currentSaveContId,
                        productCode: "GIWL",
                        aaaMembershipNumber,
                    });
                    await storeAppService(requestBody);
                } catch (error) {
                    console.warn("Error storing application:", error);
                }
                return;
            }

            if (!offerData) return;

            const applicantType = providedContactData.contactInformation?.applicantType ?? "member";
            const userRef =
                applicantType === "member" ? offerData.invitationCode : toSpouseApplicationId(offerData.invitationCode);
            const productCode: ProductCodeEnum = productConfig.productCode;

            try {
                const requestBody = createStoreAppRequest(providedContactData, { userRef, productCode });
                await storeAppService(requestBody);
            } catch (error) {
                console.warn("Error storing application:", error);
            }
        },
        [isGIWL, productConfig, offerData, dispatch]
    );

    /**
     * Submit Contact Information (Page 1)
     * Collects data from all card refs, runs product-specific verification, saves to
     * the Redux store, and pushes the data to the backend.
     * - DTO: identity check + club check, then Redux + backend
     * - MLTA: eligibility check (clubCode reused from quoteData), then Redux + backend
     * - GIWL: club code lookup only (no identity check), then Redux + backend via saveContId
     */
    const submitContactInformation = useCallback(
        (refs: ContactInformationRefs): Promise<void> => {
            // GIWL has no offerData; every other product requires it.
            if (!quoteData || (!isGIWL && !offerData)) {
                return Promise.resolve();
            }
            const collectedContactData = collectContactData(refs, contactData);

            // Save aaaMemberNumber to offerData if entered by user (MLTA only)
            const formAaaMemberNumber = collectedContactData.contactInformation?.aaaMemberNumber;
            if (isMLTA && formAaaMemberNumber && !offerData?.aaaMemberNumber) {
                dispatch(updateOfferData({ aaaMemberNumber: formAaaMemberNumber }));
            }

            return new Promise<void>((resolve) => {
                startSubmissionTransition(async () => {
                    // Process submission based on product type (GIWL needs no identity check,
                    // but still resolves a club code from the applicant's ZIP)
                    let result: SubmissionResult;
                    if (isMLTA) {
                        result = await processMLTASubmission(collectedContactData, quoteData);
                    } else if (isDTO) {
                        result = await processDTOSubmission(collectedContactData, quoteData);
                    } else {
                        result = await processGIWLSubmission(collectedContactData, quoteData);
                    }

                    dispatch(
                        saveContactInformation({
                            contactData: result.contactData,
                            needsReview: result.needsReview,
                        })
                    );

                    saveApplicationData(result.contactData);
                    resolve();
                });
            });
        },
        [dispatch, offerData, quoteData, contactData, saveApplicationData, isMLTA, isDTO, isGIWL, startSubmissionTransition]
    );

    /**
     * Loads saved application data from backend and hydrates Redux store.
     * Used when user returns to continue their application.
     * - GIWL: fetches via getApplication(saveContId)
     * - DTO/MLTA: fetches via getApplication(invitationCode)
     */
    const loadSavedApplicationData = useCallback(
        (invitationCode?: string, applicantType?: "member" | "spouse"): void => {
            const currentSaveContId = saveContIdRef.current;

            if (isGIWL) {
                if (!currentSaveContId) return;
                startDataTransition(async () => {
                    try {
                        const savedData = await getApplicationService(currentSaveContId);
                        if (Object.keys(savedData).length === 0) return;
                        const transformedContactData = transformGetDTOAppToContactData(savedData as any, "member");
                        dispatch(hydrateContactData(transformedContactData));
                    } catch (error) {
                        // Silent failure
                    }
                });
                return;
            }

            if (!invitationCode || !applicantType) return;

            startDataTransition(async () => {
                try {
                    const appInvitationCode =
                        applicantType === "member" ? invitationCode : toSpouseApplicationId(invitationCode);

                    const savedData = await getApplicationService(appInvitationCode);

                    if (Object.keys(savedData).length === 0) {
                        return;
                    }

                    const transformedContactData = transformGetDTOAppToContactData(savedData, applicantType);
                    dispatch(hydrateContactData(transformedContactData));
                } catch (error) {
                    // Silent failure - this is expected for new applications or 404s
                }
            });
        },
        [isGIWL, dispatch, startDataTransition]
    );

    return {
        // State
        contactData,
        needsReview,
        isContactDataComplete,
        isLoadingData,
        isLoadingSubmission,

        // Actions
        submitContactInformation,
        loadSavedApplicationData,
        saveApplicationData,
    };
};

// ========== Shared Helpers ==========

/**
 * Collects all contact information data from refs
 * Preserves existing policyNumber, hannoverId, and clubCode from Redux state if available
 */
const collectContactData = (refs: ContactInformationRefs, existingContactData: ContactData | null): ContactData => {
    return {
        contactInformation: refs.contactInformation.current?.getValues() || null,
        statementOfHealth: refs.statementOfHealth?.current?.getValues() || null,
        coverageReplacement: refs.coverageReplacement?.current?.getValues() || null,
        policyBeneficiary: refs.policyBeneficiary.current?.getValues() || null,
        secondaryAddressee: refs.secondaryAddressee.current?.getValues() || null,
        policyNumber: existingContactData?.policyNumber ?? null,
        hannoverId: existingContactData?.hannoverId ?? null,
        clubCode: existingContactData?.clubCode ?? null,
    };
};

/**
 * Mints a FAST policy number and stores it on contactData.
 * POST /generateFastPolicyNumber takes no request body and returns a freshly generated number.
 * Skips the call when a policy number already exists (e.g. a resumed application) so the same
 * number is reused. Returns needsReview=true on a missing/invalid number or on error.
 * Shared by MLTA and GIWL for identity verification at submission time.
 */
const generatePolicyNumber = async (contactData: ContactData): Promise<boolean> => {
    if (contactData.policyNumber) {
        return false; // Already have a policy number; no need to generate a new one
    }
    try {
        const response = await generateFastPolicyNumber();

        if (response.policyNumber && response.policyNumber.length >= 10) {
            contactData.policyNumber = response.policyNumber;
            return false; // No review needed
        }
        return true; // Needs review
    } catch (error: unknown) {
        return true; // Needs review on error
    }
};

// ========== DTO Submission Helpers ==========

/**
 * Creates identity check request body from contact info
 */
const createIdentityCheckRequest = (contactData: ContactData, quoteData: QuoteData): CheckIdentityRequestBody => {
    const contactInformation = contactData.contactInformation!;
    const birthDateObj = createDate(
        parseInt(contactInformation.dateOfBirth.year, 10),
        parseInt(contactInformation.dateOfBirth.month, 10),
        parseInt(contactInformation.dateOfBirth.day, 10)
    );

    return {
        policyNumber: contactData.policyNumber ?? undefined,
        hannoverID: contactData.hannoverId ?? undefined,
        firstName: contactInformation.firstName,
        middleInitial: undefined,
        lastName: contactInformation.lastName,
        birthDate: toLocalISODateString(birthDateObj),
        phone: cleanPhone(contactInformation.phone),
        address: {
            addressLine1: contactInformation.address,
            addressLine2: undefined,
            city: contactInformation.city,
            state: contactInformation.state,
            zipCode: contactInformation.zipCode,
            country: "USA",
        },
        gender: toSingleLetterFromLowerCaseGender(quoteData.gender ?? contactData.contactInformation?.gender),
        email: contactInformation.email,
    };
};

/**
 * Performs DTO identity check using checkIdentity endpoint
 * Requires both policyNumber and hannoverId for successful verification
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const performDTOIdentityCheck = async (contactData: ContactData, quoteData: QuoteData): Promise<boolean> => {
    try {
        const requestBody = createIdentityCheckRequest(contactData, quoteData);
        const response = await checkIdentity(requestBody);

        if (response.policyNumber && response.policyNumber.length >= 10 && response.hannoverId) {
            contactData.policyNumber = response.policyNumber;
            contactData.hannoverId = response.hannoverId;
            return false; // No review needed
        }
        return true; // Needs review
    } catch (error: unknown) {
        return true; // Needs review on error
    }
};

/**
 * Fetches club code from zip code for DTO applications
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const performDTOClubCheck = async (contactData: ContactData): Promise<boolean> => {
    const contactInformation = contactData.contactInformation!;
    try {
        const clubResponse = await getClubData(contactInformation.zipCode);
        if (clubResponse.clubCode) {
            contactData.clubCode = clubResponse.clubCode;
            return false; // No review needed
        }
        return true; // Needs review
    } catch (error: unknown) {
        return true; // Needs review on error
    }
};

/**
 * Processes DTO contact information submission
 * Performs identity check and club check, then returns the result
 */
const processDTOSubmission = async (contactData: ContactData, quoteData: QuoteData): Promise<SubmissionResult> => {
    const identityNeedsReview = await performDTOIdentityCheck(contactData, quoteData);
    const clubNeedsReview = await performDTOClubCheck(contactData);
    const needsReview = identityNeedsReview || clubNeedsReview;

    return { contactData, needsReview };
};

interface StoreAppParams {
    userRef: string;
    productCode: ProductCodeEnum;
    aaaMembershipNumber?: string;
}

const createStoreAppRequest = (contactData: ContactData, params: StoreAppParams): StoreAppRequestBody => {
    const { contactInformation, statementOfHealth, coverageReplacement, policyBeneficiary, secondaryAddressee } = contactData;

    const requestBody: StoreAppRequestBody = {
        userRef: params.userRef,
        productCode: params.productCode,
        aaaMembershipNumber: params.aaaMembershipNumber,
        policyNumber: contactData.policyNumber ?? undefined,
        hannoverId: contactData.hannoverId ?? undefined,
        paymentToken: undefined,
    };

    if (contactInformation) {
        requestBody.firstName = contactInformation.firstName || undefined;
        requestBody.lastName = contactInformation.lastName || undefined;
        requestBody.email = contactInformation.email || undefined;
        requestBody.phone = cleanPhone(contactInformation.phone) || undefined;
        requestBody.phoneType = contactInformation.phoneType || undefined;
        requestBody.tcpaConsent = contactInformation.tcpaConsent ?? undefined;
        requestBody.addressline1 = contactInformation.address || undefined;
        requestBody.city = contactInformation.city || undefined;
        requestBody.zipCd = contactInformation.zipCode || undefined;
        requestBody.gender = contactInformation.gender
            ? toSingleLetterFromLowerCaseGender(contactInformation.gender) ?? undefined
            : undefined;
    }

    if (statementOfHealth) {
        requestBody.heightFeet = statementOfHealth.heightFeet || undefined;
        requestBody.heightInches = statementOfHealth.heightInches || undefined;
        requestBody.weight = statementOfHealth.weight || undefined;
        requestBody.hasMedicalConditions = statementOfHealth.hasMedicalConditions ?? undefined;
        requestBody.hasDiagnosticTesting = statementOfHealth.hasDiagnosticTesting ?? undefined;
    }

    if (coverageReplacement) {
        requestBody.replacementIndicator = coverageReplacement.replacementIndicator ?? undefined;
        requestBody.replacementCompany = coverageReplacement.replacementCompany || undefined;
        requestBody.replacementAccount = coverageReplacement.replacementAccount || undefined;
    }

    if (policyBeneficiary) {
        requestBody.beneFirstName = policyBeneficiary.firstName || undefined;
        requestBody.beneLastName = policyBeneficiary.lastName || undefined;
        requestBody.beneRelationship = policyBeneficiary.relationship || undefined;
    }

    if (secondaryAddressee) {
        requestBody.hasSecondaryAddressee = secondaryAddressee.hasSecondaryAddressee ?? undefined;
        requestBody.secondaryFirstName = secondaryAddressee.firstName || undefined;
        requestBody.secondaryLastName = secondaryAddressee.lastName || undefined;
        requestBody.secondaryAddress = secondaryAddressee.address || undefined;
        requestBody.secondaryCity = secondaryAddressee.city || undefined;
        requestBody.secondaryState = secondaryAddressee.state || undefined;
        requestBody.secondaryZipCd = secondaryAddressee.zipCode || undefined;
        requestBody.secondaryPhone = secondaryAddressee.phone ? cleanPhone(secondaryAddressee.phone) : undefined;
    }

    return requestBody;
};

// ========== MLTA Submission Helpers ==========

/**
 * Processes MLTA contact information submission
 * Uses clubCode from quoteData (already fetched during quote step) instead of refetching
 * Mints a FAST policy number for identity verification
 */
const processMLTASubmission = async (contactData: ContactData, quoteData: QuoteData): Promise<SubmissionResult> => {
    // Use clubCode from quoteData - already fetched in useMLTAQuoteForm
    if (quoteData.clubCode) {
        contactData.clubCode = quoteData.clubCode;
    }

    // Supplement zipCode from quoteData for MLTA
    if (contactData.contactInformation) {
        contactData.contactInformation.zipCode = quoteData.zipCode!;
    }

    const needsReview = await generatePolicyNumber(contactData);

    return { contactData, needsReview };
};

// ========== GIWL Submission Helpers ==========

/**
 * Resolves the GIWL club code from the applicant's quote ZIP and stores it on contactData.
 * Legacy sourced the club code from a "cc" cookie / ZIP-based club-profile lookup; the SPA
 * mirrors that with getClubData. Non-blocking: on a missing ZIP or lookup failure it leaves
 * contactData.clubCode untouched, since the submission mapper also falls back to campaign params.
 */
const resolveGIWLClubCode = async (contactData: ContactData, quoteData: QuoteData): Promise<void> => {
    if (!quoteData.zipCode) return;
    try {
        const clubResponse = await getClubData(quoteData.zipCode);
        if (clubResponse.clubCode) {
            contactData.clubCode = clubResponse.clubCode;
        }
    } catch (error: unknown) {
        // Non-blocking: GIWL proceeds without a looked-up club code.
    }
};

/**
 * Processes GIWL contact information submission.
 * Resolves the club code from the applicant's ZIP and mints a FAST policy number, both of which
 * are sent at submission time. needsReview is driven by the policy-number step.
 */
const processGIWLSubmission = async (contactData: ContactData, quoteData: QuoteData): Promise<SubmissionResult> => {
    await resolveGIWLClubCode(contactData, quoteData);
    const needsReview = await generatePolicyNumber(contactData);
    return { contactData, needsReview };
};
